import { memo, useMemo } from 'react';

import { CopyText, Flex, Heading, Label, Link, Text } from '@/components/core';
import { InfoIcon } from '@/components/icons';
import { useGetAgentFromPath, useGetAppClientWithSecret } from '@/hooks';

export type TriggerPowerAutomateConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const ITriggerPowerAutomateConfiguration = ({}: TriggerPowerAutomateConfigurationProps) => {
  const { data: agent } = useGetAgentFromPath();
  const { data: appClient } = useGetAppClientWithSecret();
  const clientCredentials = useMemo(
    () => (appClient?.clientSecret ? btoa(`${appClient.clientId}:${appClient.clientSecret}`) : undefined),
    [appClient]
  );

  return (
    <Flex col className="gap-md p-md grow">
      <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
        <Flex col className="gap-xs">
          <Label>Client Credentials</Label>
          <CopyText isSensitive value={clientCredentials} />
        </Flex>
      </Flex>

      <div className="grow" />

      <Flex col className="group/tutorial-box mb-md rounded-lg border-1 border-gray-200 bg-gray-50 shadow-md">
        <Flex className="p-sm items-center">
          <Heading size="sm" className="gap-xs flex grow">
            <InfoIcon className="text-gray-400" />
            Setting up Power Automate
          </Heading>

          <Link href="https://docs.cradl.ai/integrations/powerautomate" className="font-regular text-sm">
            Read full guide
          </Link>
        </Flex>

        <Flex col className="p-md gap-sm rounded-lg border-1 border-gray-200 bg-white">
          <Text>
            1. Login to <Link href="https://make.powerautomate.com">Power Automate</Link>.
          </Text>
          <Text>
            2. Add the <i>Extract Data From Document</i> action to your flow.
          </Text>
          <Text>
            3. If you don't have a Cradl connection in Power Automate already, setup a new <i>Connection</i> using the
            above <i>Client Credentials</i>.
          </Text>
          <Text>
            4. Use the <i>Agent</i> dropdown and choose <i>{agent?.name}</i> then set <i>Document</i> to contents of a
            file.
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const TriggerPowerAutomateConfiguration = memo(
  ITriggerPowerAutomateConfiguration
) as typeof ITriggerPowerAutomateConfiguration;
